import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Button, Input, Typography, Textarea } from '@material-tailwind/react';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import { Alerty } from '../../common/Alerty';

const { rootUrl } = require('../../../config/config');

const postingTypeOptions = [
    { value: 'CASHBOOK_TRANSFER', label: 'Cashbook to cashbook' },
    { value: 'VENDOR_PAYMENT', label: 'Cashbook to vendor / PL' },
    { value: 'OWNER_PAYMENT', label: 'Cashbook to owner' },
    { value: 'GL_PAYMENT', label: 'Cashbook to GL' },
];

const paymentMethodOptions = [
    { value: 'CASH', label: 'Cash' },
    { value: 'MPESA', label: 'M-Pesa' },
    { value: 'BANK_TRANSFER', label: 'Bank transfer' },
    { value: 'CHEQUE', label: 'Cheque' },
];

const referenceTypeOptions = [
    { value: '', label: 'None' },
    { value: 'MANUAL', label: 'Manual' },
    { value: 'INVOICE', label: 'Invoice' },
    { value: 'REMITTANCE', label: 'Remittance' },
    { value: 'JOURNAL', label: 'Journal' },
];

const defaultFormState = {
    posting_type: 'CASHBOOK_TRANSFER',
    source_cashbook_account_id: '',
    target_cashbook_account_id: '',
    supplier_id: '',
    owner_code: '',
    owner_trans_type: '',
    gl_account_code: '',
    analysis_code: '',
    sub_analysis_code: '',
    sub_analysis: '',
    amount: '',
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    notes: '',
    reference_type: '',
    reference_id: '',
    payment_method: 'MPESA',
    payment_reference: '',
};

const CashbookNewEntry = () => {
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [formData, setFormData] = useState(defaultFormState);
    const [cashbookAccounts, setCashbookAccounts] = useState([]);
    const [owners, setOwners] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [analysisCodeOptions, setAnalysisCodeOptions] = useState([]);
    const [analysisCodeLoading, setAnalysisCodeLoading] = useState(false);
    const [subAnalysisCodeOptions, setSubAnalysisCodeOptions] = useState([]);
    const [subAnalysisCodeLoading, setSubAnalysisCodeLoading] = useState(false);
    const [ownerTypes, setOwnerTypes] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const loadOptions = async () => {
            try {
                setLoadingOptions(true);
                const headers = { Authorization: `Bearer ${authToken}` };
                const [accountsRes, ownersRes, suppliersRes, coaRes, ownerTypesRes] = await Promise.all([
                    axios.get(`${rootUrl}/api/cashbook/accounts`, { headers }),
                    axios.get(`${rootUrl}/owners/list`, { headers }),
                    axios.get(`${rootUrl}/parts/suppliers/active`, { headers }),
                    axios.get(`${rootUrl}/system/coa/accounts`, { headers }),
                    axios.get(`${rootUrl}/payments/owner-types`, { headers }),
                ]);

                const accountsData = accountsRes.data?.success ? accountsRes.data.data : (accountsRes.data?.data || accountsRes.data || []);
                const ownersData = Array.isArray(ownersRes.data?.data) ? ownersRes.data.data : (Array.isArray(ownersRes.data) ? ownersRes.data : []);
                const suppliersData = suppliersRes.data?.success ? suppliersRes.data.data : (suppliersRes.data?.data || suppliersRes.data || []);
                const coaData = coaRes.data?.success ? coaRes.data.data : (coaRes.data?.data || []);
                const ownerTypesData = ownerTypesRes.data?.data || [];

                setCashbookAccounts(Array.isArray(accountsData) ? accountsData : []);
                setOwners(Array.isArray(ownersData) ? ownersData : []);
                setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
                setGlAccounts(Array.isArray(coaData) ? coaData : []);
                setOwnerTypes(Array.isArray(ownerTypesData) ? ownerTypesData : []);
            } catch (error) {
                console.error('Error loading cashbook posting options:', error);
                setErrorMessage(error.response?.data?.message || 'Failed to load posting options');
            } finally {
                setLoadingOptions(false);
            }
        };

        loadOptions();
    }, [authToken]);

    useEffect(() => {
        if (!formData.source_cashbook_account_id && cashbookAccounts.length > 0) {
            setFormData((prev) => ({
                ...prev,
                source_cashbook_account_id: String(cashbookAccounts[0].id),
            }));
        }
    }, [cashbookAccounts, formData.source_cashbook_account_id]);

    useEffect(() => {
        if (!formData.owner_trans_type && ownerTypes.length > 0) {
            setFormData((prev) => ({
                ...prev,
                owner_trans_type: ownerTypes[0].type,
            }));
        }
    }, [ownerTypes, formData.owner_trans_type]);

    useEffect(() => {
        const loadAnalysisCodes = async () => {
            if (formData.posting_type !== 'GL_PAYMENT') {
                setAnalysisCodeOptions([]);
                updateField('analysis_code', '');
                return;
            }

            if (!formData.gl_account_code) {
                setAnalysisCodeOptions([]);
                setSubAnalysisCodeOptions([]);
                updateField('analysis_code', '');
                updateField('sub_analysis_code', '');
                updateField('sub_analysis', '');
                return;
            }

            try {
                setAnalysisCodeLoading(true);
                const response = await axios.get(
                    `${rootUrl}/api/system/coa/accounts/${formData.gl_account_code}/analysis-codes`,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
                const optionsData = response.data?.success ? response.data.data : (response.data?.data || []);
                const nextOptions = Array.isArray(optionsData)
                    ? optionsData.map((code) => ({
                        value: code.analysis_code,
                        label: `${code.analysis_code}${code.description ? ` - ${code.description}` : ''}`,
                    }))
                    : [];
                setAnalysisCodeOptions(nextOptions);

                setFormData((prev) => {
                    if (prev.analysis_code && nextOptions.some((option) => option.value === prev.analysis_code)) {
                        return prev;
                    }
                    return {
                        ...prev,
                        analysis_code: nextOptions[0]?.value || '',
                    };
                });
            } catch (error) {
                console.error('Error loading analysis codes:', error);
                setAnalysisCodeOptions([]);
                setErrorMessage(error.response?.data?.message || 'Failed to load analysis codes');
            } finally {
                setAnalysisCodeLoading(false);
            }
        };

        loadAnalysisCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, formData.posting_type, formData.gl_account_code]);

    useEffect(() => {
        const loadSubAnalysisCodes = async () => {
            if (formData.posting_type !== 'GL_PAYMENT') {
                setSubAnalysisCodeOptions([]);
                updateField('sub_analysis_code', '');
                updateField('sub_analysis', '');
                return;
            }

            try {
                setSubAnalysisCodeLoading(true);
                const response = await axios.get(
                    `${rootUrl}/api/system/gl-sub-analysis-codes`,
                    { headers: { Authorization: `Bearer ${authToken}` } }
                );
                const optionsData = response.data?.success ? response.data.data : (response.data?.data || []);
                const nextOptions = Array.isArray(optionsData)
                    ? optionsData
                        .filter((code) => code.is_active !== false)
                        .map((code) => ({
                            value: code.code,
                            label: `${code.code}${code.description ? ` - ${code.description}` : ''}`,
                        }))
                    : [];
                setSubAnalysisCodeOptions(nextOptions);

                setFormData((prev) => {
                    if (prev.sub_analysis_code && nextOptions.some((option) => option.value === prev.sub_analysis_code)) {
                        return prev;
                    }
                    return {
                        ...prev,
                        sub_analysis_code: nextOptions[0]?.value || '',
                    };
                });
            } catch (error) {
                console.error('Error loading sub-analysis codes:', error);
                setSubAnalysisCodeOptions([]);
                setErrorMessage(error.response?.data?.message || 'Failed to load sub-analysis codes');
            } finally {
                setSubAnalysisCodeLoading(false);
            }
        };

        loadSubAnalysisCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, formData.posting_type]);

    const clearFieldError = (field) => {
        setErrors((prev) => {
            if (!prev[field]) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const updateField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        clearFieldError(field);
    };

    const updatePostingType = (value) => {
        setFormData((prev) => ({
            ...prev,
            posting_type: value,
            target_cashbook_account_id: '',
            supplier_id: '',
            owner_code: '',
            owner_trans_type: ownerTypes[0]?.type || '',
            gl_account_code: '',
            analysis_code: '',
            sub_analysis_code: '',
            sub_analysis: '',
        }));
        setErrors({});
    };

    const cashbookAccountOptions = useMemo(() => (
        cashbookAccounts.map((account) => ({
            value: String(account.id),
            label: `${account.account_name} (${account.account_type})`,
        }))
    ), [cashbookAccounts]);

    const ownerOptions = useMemo(() => (
        owners.map((owner) => ({
            value: owner.code,
            label: `${owner.code} - ${owner.name}`,
        }))
    ), [owners]);

    const supplierOptions = useMemo(() => (
        suppliers.map((supplier) => ({
            value: String(supplier.id),
            label: `${supplier.code} - ${supplier.name}`,
        }))
    ), [suppliers]);

    const glAccountOptions = useMemo(() => (
        glAccounts.map((account) => ({
            value: account.account_code,
            label: `${account.account_code} - ${account.account_name}`,
        }))
    ), [glAccounts]);

    const analysisCodeSelectOptions = useMemo(() => (
        analysisCodeOptions
    ), [analysisCodeOptions]);

    const ownerTypeOptions = useMemo(() => (
        ownerTypes.map((type) => ({
            value: type.type,
            label: `${type.type}${type.short_desc ? ` - ${type.short_desc}` : ''}`,
        }))
    ), [ownerTypes]);

    const selectedPostingType = postingTypeOptions.find((option) => option.value === formData.posting_type) || postingTypeOptions[0];

    const validate = () => {
        const nextErrors = {};

        if (!formData.source_cashbook_account_id) {
            nextErrors.source_cashbook_account_id = 'Source cashbook account is required';
        }
        if (!formData.amount || Number.parseFloat(formData.amount) <= 0) {
            nextErrors.amount = 'Amount must be greater than zero';
        }
        if (!formData.entry_date) {
            nextErrors.entry_date = 'Date is required';
        }
        if (!formData.payment_reference?.trim()) {
            nextErrors.payment_reference = 'Payment reference is required';
        }
        if (formData.posting_type === 'CASHBOOK_TRANSFER' && !formData.target_cashbook_account_id) {
            nextErrors.target_cashbook_account_id = 'Target cashbook account is required';
        }
        if (formData.posting_type === 'VENDOR_PAYMENT' && !formData.supplier_id) {
            nextErrors.supplier_id = 'Vendor is required';
        }
        if (formData.posting_type === 'OWNER_PAYMENT') {
            if (!formData.owner_code) {
                nextErrors.owner_code = 'Owner is required';
            }
            if (!formData.owner_trans_type) {
                nextErrors.owner_trans_type = 'Owner transaction type is required';
            }
        }
        if (formData.posting_type === 'GL_PAYMENT' && !formData.gl_account_code) {
            nextErrors.gl_account_code = 'GL account is required';
        }
        if (formData.posting_type === 'GL_PAYMENT' && !formData.analysis_code) {
            nextErrors.analysis_code = 'Analysis code is required';
        }
        if (formData.posting_type === 'GL_PAYMENT' && !formData.sub_analysis_code) {
            nextErrors.sub_analysis_code = 'Sub-analysis type is required';
        }
        if (formData.posting_type === 'GL_PAYMENT' && !formData.sub_analysis?.trim()) {
            nextErrors.sub_analysis = 'Sub-analysis value is required';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validate()) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const payload = {
                posting_type: formData.posting_type,
                source_cashbook_account_id: Number(formData.source_cashbook_account_id),
                target_cashbook_account_id: formData.target_cashbook_account_id ? Number(formData.target_cashbook_account_id) : null,
                supplier_id: formData.supplier_id ? Number(formData.supplier_id) : null,
                owner_code: formData.owner_code || null,
                owner_trans_type: formData.owner_trans_type || null,
                gl_account_code: formData.gl_account_code || null,
                analysis_code: formData.analysis_code || null,
                sub_analysis_code: formData.sub_analysis_code || null,
                sub_analysis: formData.sub_analysis.trim() || null,
                amount: Number.parseFloat(formData.amount),
                entry_date: formData.entry_date,
                description: formData.description || '',
                notes: formData.notes || '',
                reference_type: formData.reference_type || null,
                reference_id: formData.reference_id || null,
                payment_method: formData.payment_method,
                payment_reference: formData.payment_reference.trim(),
                input_by: user?.username || 'system',
            };

            const response = await axios.post(
                `${rootUrl}/api/cashbook/postings`,
                payload,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );

            if (response.data?.success) {
                setSuccessMessage(response.data.message || 'Cashbook posting saved successfully');
                const sourceAccountId = payload.source_cashbook_account_id;
                navigate(`/cashbook?accountId=${sourceAccountId}`);
                return;
            }

            setErrorMessage(response.data?.message || 'Failed to create posting');
        } catch (error) {
            console.error('Error creating cashbook posting:', error);
            setErrorMessage(error.response?.data?.message || 'Failed to create posting');
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '44px',
            borderRadius: '12px',
            borderColor: state.isFocused ? '#FF5733' : '#e5e7eb',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#FF5733',
            },
        }),
    };

    const renderTypeFields = () => {
        if (formData.posting_type === 'CASHBOOK_TRANSFER') {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Target cashbook account</Typography>
                        <Select
                            inputId="target_cashbook_account_id"
                            options={cashbookAccountOptions}
                            value={cashbookAccountOptions.find((option) => option.value === formData.target_cashbook_account_id) || null}
                            onChange={(option) => updateField('target_cashbook_account_id', option?.value || '')}
                            placeholder="Select target account..."
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                        {errors.target_cashbook_account_id && <p className="text-xs text-red-500">{errors.target_cashbook_account_id}</p>}
                    </div>
                </div>
            );
        }

        if (formData.posting_type === 'VENDOR_PAYMENT') {
            return (
                <div className="space-y-1.5">
                    <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Vendor / PL ledger</Typography>
                    <Select
                        inputId="supplier_id"
                        options={supplierOptions}
                        value={supplierOptions.find((option) => option.value === formData.supplier_id) || null}
                        onChange={(option) => updateField('supplier_id', option?.value || '')}
                        placeholder="Select vendor..."
                        classNamePrefix="react-select"
                        styles={selectStyles}
                    />
                    {errors.supplier_id && <p className="text-xs text-red-500">{errors.supplier_id}</p>}
                </div>
            );
        }

        if (formData.posting_type === 'OWNER_PAYMENT') {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Owner</Typography>
                        <Select
                            inputId="owner_code"
                            options={ownerOptions}
                            value={ownerOptions.find((option) => option.value === formData.owner_code) || null}
                            onChange={(option) => updateField('owner_code', option?.value || '')}
                            placeholder="Select owner..."
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                        {errors.owner_code && <p className="text-xs text-red-500">{errors.owner_code}</p>}
                    </div>
                    <div className="space-y-1.5">
                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Owner transaction type</Typography>
                        <Select
                            inputId="owner_trans_type"
                            options={ownerTypeOptions}
                            value={ownerTypeOptions.find((option) => option.value === formData.owner_trans_type) || null}
                            onChange={(option) => updateField('owner_trans_type', option?.value || '')}
                            placeholder="Select transaction type..."
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                        {errors.owner_trans_type && <p className="text-xs text-red-500">{errors.owner_trans_type}</p>}
                    </div>
                </div>
            );
        }

        if (formData.posting_type === 'GL_PAYMENT') {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">GL account</Typography>
                        <Select
                            inputId="gl_account_code"
                            options={glAccountOptions}
                            value={glAccountOptions.find((option) => option.value === formData.gl_account_code) || null}
                            onChange={(option) => updateField('gl_account_code', option?.value || '')}
                            placeholder="Select GL account..."
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                        {errors.gl_account_code && <p className="text-xs text-red-500">{errors.gl_account_code}</p>}
                    </div>
                    <div className="space-y-1.5">
                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Analysis code</Typography>
                        <Select
                            inputId="analysis_code"
                            options={analysisCodeSelectOptions}
                            value={analysisCodeSelectOptions.find((option) => option.value === formData.analysis_code) || null}
                            onChange={(option) => updateField('analysis_code', option?.value || '')}
                            placeholder={formData.gl_account_code ? 'Select analysis code...' : 'Select a GL account first'}
                            isDisabled={!formData.gl_account_code || analysisCodeLoading}
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                        {analysisCodeLoading && <p className="text-xs text-slate-500">Loading analysis codes...</p>}
                        {!analysisCodeLoading && formData.gl_account_code && analysisCodeSelectOptions.length === 0 && (
                            <p className="text-xs text-amber-600">No analysis codes are configured for this GL account.</p>
                        )}
                        {errors.analysis_code && <p className="text-xs text-red-500">{errors.analysis_code}</p>}
                    </div>
                    <div className="space-y-1.5">
                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Sub-analysis type</Typography>
                        <Select
                            inputId="sub_analysis_code"
                            options={subAnalysisCodeOptions}
                            value={subAnalysisCodeOptions.find((option) => option.value === formData.sub_analysis_code) || null}
                            onChange={(option) => updateField('sub_analysis_code', option?.value || '')}
                            placeholder="Select sub-analysis type..."
                            isDisabled={subAnalysisCodeLoading}
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                        {subAnalysisCodeLoading && <p className="text-xs text-slate-500">Loading sub-analysis types...</p>}
                        {!subAnalysisCodeLoading && subAnalysisCodeOptions.length === 0 && (
                            <p className="text-xs text-amber-600">No sub-analysis types are configured.</p>
                        )}
                        {errors.sub_analysis_code && <p className="text-xs text-red-500">{errors.sub_analysis_code}</p>}
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Sub-analysis value</Typography>
                        <Input
                            type="text"
                            value={formData.sub_analysis}
                            onChange={(event) => updateField('sub_analysis', event.target.value)}
                            placeholder="Enter sub-analysis value..."
                            className="!border-gray-200 focus:!border-[#FF5733]"
                        />
                        {errors.sub_analysis && <p className="text-xs text-red-500">{errors.sub_analysis}</p>}
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-5xl space-y-6">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <Typography className="text-4xl font-bold text-[#2a2e30]">New Cashbook Posting</Typography>
                        <div className="mt-2 h-1 w-24 rounded-full bg-[#FF5733]" />
                        <Typography className="mt-3 text-sm text-slate-600">
                            Create one cashbook-ledger posting and the linked records in a single transaction.
                        </Typography>
                    </div>
                    <Button
                        type="button"
                        onClick={() => navigate('/cashbook')}
                        className="flex items-center gap-2 rounded-sm bg-white text-[#FF5733] shadow-none border border-[#FF5733] hover:bg-orange-50"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Back
                    </Button>
                </div>

                {successMessage && <Alerty open={Boolean(successMessage)} color="green" message={successMessage} setOpen={() => setSuccessMessage('')} />}
                {errorMessage && <Alerty open={Boolean(errorMessage)} color="red" message={errorMessage} setOpen={() => setErrorMessage('')} />}

                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                    <CardBody className="p-5 sm:p-6">
                        {loadingOptions ? (
                            <div className="flex items-center gap-3 text-slate-500">
                                <FontAwesomeIcon icon={faSpinner} spin />
                                Loading posting options...
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5 md:col-span-2">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Posting type</Typography>
                                        <Select
                                            inputId="posting_type"
                                            options={postingTypeOptions}
                                            value={selectedPostingType}
                                            onChange={(option) => updatePostingType(option?.value || 'CASHBOOK_TRANSFER')}
                                            placeholder="Select posting type..."
                                            classNamePrefix="react-select"
                                            styles={selectStyles}
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Source cashbook account</Typography>
                                        <Select
                                            inputId="source_cashbook_account_id"
                                            options={cashbookAccountOptions}
                                            value={cashbookAccountOptions.find((option) => option.value === formData.source_cashbook_account_id) || null}
                                            onChange={(option) => updateField('source_cashbook_account_id', option?.value || '')}
                                            placeholder="Select source account..."
                                            classNamePrefix="react-select"
                                            styles={selectStyles}
                                        />
                                        {errors.source_cashbook_account_id && <p className="text-xs text-red-500">{errors.source_cashbook_account_id}</p>}
                                    </div>

                                    <div className="space-y-1.5">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Amount</Typography>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={formData.amount}
                                            onChange={(event) => updateField('amount', event.target.value)}
                                            placeholder="0.00"
                                            className="!border-gray-200 focus:!border-[#FF5733]"
                                        />
                                        {errors.amount && <p className="text-xs text-red-500">{errors.amount}</p>}
                                    </div>

                                    <div className="space-y-1.5">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Date</Typography>
                                        <Input
                                            type="date"
                                            value={formData.entry_date}
                                            onChange={(event) => updateField('entry_date', event.target.value)}
                                            className="!border-gray-200 focus:!border-[#FF5733]"
                                        />
                                        {errors.entry_date && <p className="text-xs text-red-500">{errors.entry_date}</p>}
                                    </div>

                                    <div className="space-y-1.5 md:col-span-2">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Narration</Typography>
                                        <Textarea
                                            value={formData.description}
                                            onChange={(event) => updateField('description', event.target.value)}
                                            rows={3}
                                            placeholder="Describe the posting..."
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Payment method</Typography>
                                        <Select
                                            inputId="payment_method"
                                            options={paymentMethodOptions}
                                            value={paymentMethodOptions.find((option) => option.value === formData.payment_method) || null}
                                            onChange={(option) => updateField('payment_method', option?.value || 'MPESA')}
                                            placeholder="Select payment method..."
                                            classNamePrefix="react-select"
                                            styles={selectStyles}
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Payment reference <span className="text-red-500">*</span></Typography>
                                        <Input
                                            type="text"
                                            value={formData.payment_reference}
                                            onChange={(event) => updateField('payment_reference', event.target.value)}
                                            required
                                            placeholder="Reference or receipt number"
                                            className="!border-gray-200 focus:!border-[#FF5733]"
                                        />
                                        {errors.payment_reference && <p className="text-xs text-red-500">{errors.payment_reference}</p>}
                                    </div>

                                    <div className="space-y-1.5">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Reference type</Typography>
                                        <Select
                                            inputId="reference_type"
                                            options={referenceTypeOptions}
                                            value={referenceTypeOptions.find((option) => option.value === formData.reference_type) || null}
                                            onChange={(option) => updateField('reference_type', option?.value || '')}
                                            placeholder="Optional reference type..."
                                            classNamePrefix="react-select"
                                            styles={selectStyles}
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Reference ID</Typography>
                                        <Input
                                            type="text"
                                            value={formData.reference_id}
                                            onChange={(event) => updateField('reference_id', event.target.value)}
                                            placeholder="Optional reference ID"
                                            className="!border-gray-200 focus:!border-[#FF5733]"
                                        />
                                    </div>

                                    <div className="space-y-1.5 md:col-span-2">
                                        <Typography className="text-xs font-bold uppercase tracking-wide text-slate-500">Notes</Typography>
                                        <Textarea
                                            value={formData.notes}
                                            onChange={(event) => updateField('notes', event.target.value)}
                                            rows={2}
                                            placeholder="Optional internal notes..."
                                        />
                                    </div>
                                </div>

                                <div className="rounded-xl border border-dashed border-[#eadfd8] bg-[#fffaf7] p-4">
                                    <Typography className="text-sm font-semibold text-[#2a2e30] mb-3">
                                        {selectedPostingType.label}
                                    </Typography>
                                    {renderTypeFields()}
                                </div>

                                <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                                    <Button
                                        type="button"
                                        onClick={() => navigate('/cashbook')}
                                        className="rounded-sm bg-slate-100 text-slate-700 shadow-none hover:bg-slate-200"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center justify-center gap-2 rounded-sm bg-[#FF5733] shadow-none hover:bg-[#e94d22]"
                                    >
                                        {submitting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                                        Post transaction
                                    </Button>
                                </div>
                            </form>
                        )}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default CashbookNewEntry;
